<template>
  <div>
    <Spinner v-if="!appLoaded" />
    <div v-else>
      <Header></Header>
      <router-view />
      <Footer ref="FooterRef"></Footer>
      <CookieConsentBanner @accepted="onConsentAccepted" />
      <vue-toastr ref="toastr"></vue-toastr>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import Header from "./components/Header.vue";
import Footer from "./components/Footer.vue";
import Spinner from "./components/Widgets/Spinner.vue";
import CookieConsentBanner from "./components/CookieConsentBanner.vue";
import store from "./store";
import TranslationHelper from "./helpers/translation";
import { setTheme } from "./helpers/theme";
import { initMatomo, trackMatomoPageView } from "./helpers/matomo";
import {
  initGoogleAnalytics,
  trackGoogleAnalyticsPageView,
} from "./helpers/analytics";
import {
  hasAnalyticsConsent,
  hasGoogleAnalyticsConfigured,
  initializeConsentDefaults,
  initIframeConsentBridge,
  restorePreviousConsent,
} from "./helpers/consent";

export default {
  name: "App",
  components: {
    Header,
    Footer,
    Spinner,
    CookieConsentBanner,
  },
  data() {
    return {
      appLoaded: false,
      teardownConsentBridge: null,
    };
  },
  mounted() {
    this.setDocumentLang();
    this.$toastr.defaultPosition = "toast-top-center";
  },
  beforeDestroy() {
    if (typeof this.teardownConsentBridge === "function") {
      this.teardownConsentBridge();
    }
    document.removeEventListener("consentGranted", this.onConsentAccepted);
  },
  watch: {
    "$i18n.locale"() {
      this.setDocumentLang();
    },
  },
  async beforeMount() {
    const urlParams = new URLSearchParams(window.location.search);
    const locale = localStorage.getItem("locale") || "en";
    const listId = urlParams.get("list_id") || store.getters["config/bookingEngineId"];

    const domains = (process.env.VUE_APP_DOMAINS || "").split(",");
    const domain = window.location.hostname;

    if (!domains.includes(domain) && window.location.pathname === "/") {
      this.$router.push({ path: "/listing" });
    }

    await Promise.all([
      TranslationHelper.updateTranslations(this.$router, locale),
      setTheme(listId, !domains.includes(domain) ? domain : null),
    ]);

    const config = store.state.config.currentConfig;
    initializeConsentDefaults(config);
    restorePreviousConsent(config);
    if (hasGoogleAnalyticsConfigured(config)) {
      // Parent hotel consent (postMessage / ?cookieConsent=) applies to Google analytics only.
      this.teardownConsentBridge = initIframeConsentBridge(config);
      document.addEventListener("consentGranted", this.onConsentAccepted);
    }
    await this.startMatomo(config);
    await this.startGoogleAnalytics(config);

    this.appLoaded = true;
    this.setDocumentLang();
  },
  methods: {
    setDocumentLang() {
      const loc =
        this.$i18n.locale ||
        localStorage.getItem("locale") ||
        "en";
      document.documentElement.setAttribute("lang", loc);
    },
    async startMatomo(config = store.state.config.currentConfig) {
      await initMatomo(Vue, config);
      trackMatomoPageView();
    },
    async startGoogleAnalytics(config = store.state.config.currentConfig) {
      if (!hasAnalyticsConsent(config)) {
        return;
      }

      initGoogleAnalytics(config);
      trackGoogleAnalyticsPageView(config);
    },
    async onConsentAccepted() {
      await this.startGoogleAnalytics(store.state.config.currentConfig);
    },
  },
};
</script>

<style>
@import url(assets/css/style.css);
@import url(assets/css/main.css);
@import url(assets/css/media.css);
@import url(assets/fonts/stylesheet.css);

.hero-image {
  height: 100vh;
  position: relative;
  background-image: url(./assets/images/banner-img.jpg);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: bottom;
}
.hero-image:before {
  background-color: rgba(255, 118, 43, 0.8);
  content: "";
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  position: absolute;
}
.toast-container > div {
  opacity: 1 !important;
}
</style>
